import React, { type ComponentType, type ReactNode, createContext } from "react";
import type { ParsedLinkHref } from "../../tiptap/link";
import {
    extractVimeoVideoId,
    extractYouTubeVideoId,
    normalizeApplePodcastEmbedUrl,
} from "../../utils/blogPostPreview";

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export type LinkComponent = ComponentType<{
    href: string;
    parsed: ParsedLinkHref;
    title?: string | null;
    children: ReactNode;
}>;

export type ImageComponent = ComponentType<{
    src: string;
    alt?: string | null;
    title?: string | null;
}>;

export type MediaEmbedKind = "youtube" | "vimeo" | "video" | "applePodcast";

export type MediaEmbedComponent = ComponentType<{
    kind: MediaEmbedKind;
    url: string;
    variant?: "episode" | "show" | null;
}>;

export interface ProseComponents {
    Paragraph: ComponentType<{ children: ReactNode }>;
    Heading: ComponentType<{ level: HeadingLevel; children: ReactNode }>;
    Blockquote: ComponentType<{ children: ReactNode }>;
    BulletList: ComponentType<{ children: ReactNode }>;
    OrderedList: ComponentType<{ start?: number; children: ReactNode }>;
    ListItem: ComponentType<{ children: ReactNode }>;
    HorizontalRule: ComponentType;
    HardBreak: ComponentType;

    Bold: ComponentType<{ children: ReactNode }>;
    Italic: ComponentType<{ children: ReactNode }>;
    Underline: ComponentType<{ children: ReactNode }>;
    Strike: ComponentType<{ children: ReactNode }>;
    Code: ComponentType<{ children: ReactNode }>;

    Table: ComponentType<{ children: ReactNode }>;
    TableHead: ComponentType<{ children: ReactNode }>;
    TableBody: ComponentType<{ children: ReactNode }>;
    TableRow: ComponentType<{ children: ReactNode }>;
    TableCell: ComponentType<{ colSpan?: number; rowSpan?: number; children: ReactNode }>;
    TableHeader: ComponentType<{ colSpan?: number; rowSpan?: number; children: ReactNode }>;

    Link: LinkComponent;
    Image: ImageComponent;
    MediaEmbed: MediaEmbedComponent;

    CustomBlock: ComponentType<{ kind: string; version: number; children: ReactNode }>;
}

export type ProseRenderConfig = Partial<ProseComponents>;

export const ProseRenderConfigContext = createContext<ProseRenderConfig | null>(null);

export function ProseRenderConfigProvider({
    config,
    children,
}: {
    config: ProseRenderConfig;
    children: ReactNode;
}) {
    return (
        <ProseRenderConfigContext.Provider value={config}>
            {children}
        </ProseRenderConfigContext.Provider>
    );
}

const KIND_LABELS: Record<MediaEmbedKind, string> = {
    youtube: "YouTube",
    vimeo: "Vimeo",
    video: "Video",
    applePodcast: "Apple Podcasts",
};

const DefaultLink: LinkComponent = ({ href, parsed, title, children }) => {
    // Unsafe schemes (javascript:, data:, vbscript:, ...) render as inert
    // text. Refusing to emit an anchor at all is safer than relying on
    // browser/React quirks to strip the navigation.
    if (parsed.kind === "unsafe") {
        return (
            <span title={title ?? undefined} data-unsafe-link={href}>
                {children}
            </span>
        );
    }
    // A content ref (article:123, PRTL-981) only becomes a page through an
    // app's route table; a consumer without a Link override renders it inert
    // rather than emitting an anchor to an unknown protocol.
    if (parsed.kind === "content") {
        return (
            <span title={title ?? undefined} data-content-link={href}>
                {children}
            </span>
        );
    }
    const isInternal = parsed.kind === "internal";
    return (
        <a
            href={href}
            title={title ?? undefined}
            {...(!isInternal && { target: "_blank", rel: "noopener noreferrer" })}
        >
            {children}
        </a>
    );
};

const DefaultImage: ImageComponent = ({ src, alt, title }) => (
    <img src={src} alt={alt ?? ""} title={title ?? undefined} />
);

const IFRAME_BASE_STYLE: React.CSSProperties = {
    border: 0,
    width: "100%",
    maxWidth: "100%",
    display: "block",
};

const VIDEO_FRAME_STYLE: React.CSSProperties = {
    ...IFRAME_BASE_STYLE,
    aspectRatio: "16 / 9",
};

function MediaEmbedFallback({ kind, url }: { kind: MediaEmbedKind; url: string }) {
    return (
        <div className="blog-embed blog-embed-error media-embed-placeholder">
            ▶ {KIND_LABELS[kind]} embed: {url || "(no URL set)"}
        </div>
    );
}

const DefaultMediaEmbed: MediaEmbedComponent = ({ kind, url, variant }) => {
    if (!url) return <MediaEmbedFallback kind={kind} url={url} />;

    if (kind === "youtube") {
        const videoId = extractYouTubeVideoId(url);
        if (!videoId) return <MediaEmbedFallback kind={kind} url={url} />;
        return (
            <iframe
                className="blog-embed blog-embed-youtube"
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                style={VIDEO_FRAME_STYLE}
            />
        );
    }

    if (kind === "vimeo") {
        const videoId = extractVimeoVideoId(url);
        if (!videoId) return <MediaEmbedFallback kind={kind} url={url} />;
        return (
            <iframe
                className="blog-embed blog-embed-vimeo"
                src={`https://player.vimeo.com/video/${videoId}`}
                title="Vimeo video"
                loading="lazy"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                style={VIDEO_FRAME_STYLE}
            />
        );
    }

    if (kind === "applePodcast") {
        const embedUrl = normalizeApplePodcastEmbedUrl(url);
        if (!embedUrl) return <MediaEmbedFallback kind={kind} url={url} />;
        const isShow = variant === "show";
        return (
            <iframe
                className={`blog-embed blog-embed-apple-podcast ${
                    isShow ? "blog-embed-apple-podcast-show" : "blog-embed-apple-podcast-episode"
                }`}
                src={embedUrl}
                title="Apple Podcasts player"
                loading="lazy"
                height={isShow ? 450 : 175}
                allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ ...IFRAME_BASE_STYLE, height: isShow ? 450 : 175 }}
            />
        );
    }

    // kind === "video" — direct hosted URL
    return (
        <video
            className="blog-embed blog-embed-video"
            src={url}
            controls
            preload="metadata"
            style={{ width: "100%", maxWidth: "100%", display: "block" }}
        />
    );
};

export const defaultProseComponents: ProseComponents = {
    Paragraph: ({ children }) => <p>{children}</p>,
    Heading: ({ level, children }) => {
        const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        return <Tag>{children}</Tag>;
    },
    Blockquote: ({ children }) => <blockquote>{children}</blockquote>,
    BulletList: ({ children }) => <ul>{children}</ul>,
    OrderedList: ({ start, children }) => <ol start={start}>{children}</ol>,
    ListItem: ({ children }) => <li>{children}</li>,
    HorizontalRule: () => <hr />,
    HardBreak: () => <br />,
    Bold: ({ children }) => <strong>{children}</strong>,
    Italic: ({ children }) => <em>{children}</em>,
    Underline: ({ children }) => <u>{children}</u>,
    Strike: ({ children }) => <s>{children}</s>,
    Code: ({ children }) => <code>{children}</code>,
    Table: ({ children }) => <table>{children}</table>,
    TableHead: ({ children }) => <thead>{children}</thead>,
    TableBody: ({ children }) => <tbody>{children}</tbody>,
    TableRow: ({ children }) => <tr>{children}</tr>,
    TableCell: ({ colSpan, rowSpan, children }) => (
        <td colSpan={colSpan} rowSpan={rowSpan}>
            {children}
        </td>
    ),
    TableHeader: ({ colSpan, rowSpan, children }) => (
        <th colSpan={colSpan} rowSpan={rowSpan}>
            {children}
        </th>
    ),
    Link: DefaultLink,
    Image: DefaultImage,
    MediaEmbed: DefaultMediaEmbed,
    CustomBlock: ({ kind, children }) => <div data-custom-block-kind={kind}>{children}</div>,
};
